import React from 'react'
import { styled, ThemeProvider, useTheme } from '@mui/material/styles'
import Box from '@mui/material/Box'
import MuiDrawer from '@mui/material/Drawer'
import List from '@mui/material/List'
import CssBaseline from '@mui/material/CssBaseline'
import IconButton from '@mui/material/IconButton'

import HomeIcon from '@mui/icons-material/Home';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';

import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import SmsOutlinedIcon from '@mui/icons-material/SmsOutlined';
import SmsIcon from '@mui/icons-material/Sms';
import ListItem from '@mui/material/ListItem'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import { NavLink } from 'react-router-dom'
import { useSidebar } from '../../../contexts/SidebarContext'

// Import icons
import FolderIcon from '@mui/icons-material/Folder';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';

import Inventory2Icon from '@mui/icons-material/Inventory2';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';

import SettingsIcon from '@mui/icons-material/Settings';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';

import folderOpen from '../../../icons/FolderOpen.svg'
import folderOpenFilled from '../../../icons/FolderOpenFilled.svg'

import archive from '../../../icons/Archive.svg'
import archiveFilled from '../../../icons/ArchiveFilled.svg'

import cog from '../../../icons/Cog.svg'
import cogFilled from '../../../icons/CogFilled.svg'

import HistoryEduOutlined from '@mui/icons-material/HistoryEduOutlined';

import { ArtosTheme } from '../../Themes'
import { Sms } from '@mui/icons-material'

const drawerWidth = 200

const openedMixin = (theme) => ({
    width: drawerWidth,
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
    }),
    overflowX: 'hidden',
})

const closedMixin = (theme) => ({
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    overflowX: 'hidden',
    // width: `calc(${theme.spacing(7)} + 1px)`,
    width: '64px',
})

const DrawerHeader = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    padding: theme.spacing(0, 1),
    ...theme.mixins.toolbar,
}))

const Drawer = styled(MuiDrawer, {
    shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    height: '100%',
    ...(open && {
        ...openedMixin(theme),
        '& .MuiDrawer-paper': openedMixin(theme),
    }),
    ...(!open && {
        ...closedMixin(theme),
        '& .MuiDrawer-paper': closedMixin(theme),
    }),
    '& .MuiDrawer-paper': {
        position: 'relative',
        height: '100%',
        overflowY: 'auto',
    },
}))

const menuItems = [
    { text: 'Workspaces', selectedIcon: <HomeIcon />, unselectedIcon: <HomeOutlinedIcon />, path: '/' },
    { text: 'Templates', selectedIcon: <Inventory2Icon />, unselectedIcon: <Inventory2OutlinedIcon />, path: '/templates' },
    { text: 'Files', selectedIcon: <FolderIcon />, unselectedIcon: <FolderOpenIcon />, path: '/source-files' },
    { text: 'Chat', selectedIcon: <SmsIcon />, unselectedIcon: <SmsOutlinedIcon/>, path: '/chat'},
    { text: 'Settings', selectedIcon: <SettingsIcon />, unselectedIcon: <SettingsOutlinedIcon />, path: '/settings' },
]

const futureMenuItems = [
    { text: 'Workspaces', selectedIcon: <HomeIcon />, unselectedIcon: <HomeOutlinedIcon />, path: '/' },
    { text: 'Templates', selectedIcon: <Inventory2Icon />, unselectedIcon: <Inventory2OutlinedIcon />, path: '/templates' },
    { text: 'Files', selectedIcon: <FolderIcon />, unselectedIcon: <FolderOpenIcon />, path: '/source-files' },
    { text: 'Settings', selectedIcon: <SettingsIcon />, unselectedIcon: <SettingsOutlinedIcon />, path: '/settings' },
]

const adminMenuItems = [
    { text: 'Workspaces', icon: <HomeIcon />, path: '/' },
    { text: 'Templates', icon: archive, path: '/templates' },
    { text: 'Source Files', icon: folderOpen, path: '/source-files' },
    { text: 'Settings', icon: cog, path: '/settings' },
]
const adminMenuItemsExperiment = [
    { text: 'Dashboard', icon: <HomeIcon />, path: '/' },
    { text: 'Document Sets', icon: <HistoryEduOutlined />, path: '/document-sets' },
    { text: 'Templates', icon: archive, path: '/templates' },
    { text: 'Source Files', icon: folderOpen, path: '/source-files' },
    { text: 'Settings', icon: cog, path: '/settings' },
]

export default function Sidebar() {
    const theme = useTheme()
    const { isOpen, toggleSidebar } = useSidebar()

    return (
        <Box sx={{ height: '100%', overflow: 'hidden', flexShrink: 0 }}>
            <ThemeProvider theme={ArtosTheme}>
                <CssBaseline />
                <Drawer variant="permanent" open={isOpen}>
                    <DrawerHeader>
                        <IconButton onClick={toggleSidebar} sx={{ padding: '0px 10px 0px 10px' }}>
                            {!isOpen ? (
                                <KeyboardDoubleArrowRightIcon />
                            ) : (
                                <KeyboardDoubleArrowLeftIcon />
                            )}
                        </IconButton>
                    </DrawerHeader>
                    <List sx={{ flexGrow: 1, overflow: 'hidden', padding: '0px' }}>
                        {menuItems.map((item, index) => (
                            <ListItem
                                key={item.text}
                                disablePadding = {true}
                                sx={{ display: 'block' }}
                            >
                                <NavLink
                                    to={item.path}
                                    style={{
                                        textDecoration: 'none',
                                        color: 'inherit',
                                    }}
                                >
                                    {({ isActive }) => (
                                        <ListItemButton
                                            sx={{
                                                minHeight: '48px',
                                                justifyContent: isOpen
                                                    ? 'initial'
                                                    : 'center',
                                                px: '16px',
                                                py: '12px',
                                                backgroundColor: isActive
                                                    ? theme.palette.action
                                                          .selected
                                                    : 'transparent',
                                            }}
                                        >
                                            <ListItemIcon
                                                sx={{
                                                    minWidth: 0,
                                                    mr: isOpen ? 3 : 'auto',
                                                    justifyContent: 'center',
                                                }}
                                            >
                                                {typeof item.selectedIcon === 'string'  
                                                    ? <img
                                                            src={isActive ? item.selectedIcon : item.unselectedIcon}
                                                            alt={isActive ? item.text + ' (Selected)' : item.text + ' (Unselected)'}
                                                            className="nav-logo"
                                                            style={{ display:'flex', alignItems:'center', justifyContent:'center' }}
                                                        />
                                                    : isActive
                                                        ? item.selectedIcon
                                                        : item.unselectedIcon
                                                }
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={item.text}
                                                sx={{ opacity: isOpen ? 1 : 0, display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}
                                            />
                                        </ListItemButton>
                                    )}
                                </NavLink>
                            </ListItem>
                        ))}
                    </List>
                </Drawer>
            </ThemeProvider>
        </Box>
    )
}
